import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Avatar,
} from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/firebase/auth';

type ProfileDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const ProfileDialog = ({ open, onClose }: ProfileDialogProps) => {
  const { user } = useAuth();
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Parse existing display name into given/family name
  useEffect(() => {
    if (user?.displayName) {
      const parts = user.displayName.trim().split(' ');
      if (parts.length >= 2) {
        setGivenName(parts[0]);
        setFamilyName(parts.slice(1).join(' '));
      } else {
        setGivenName(user.displayName);
        setFamilyName('');
      }
    } else {
      setGivenName('');
      setFamilyName('');
    }
  }, [user?.displayName, open]);

  const handleClose = () => {
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedGiven = givenName.trim();
    const trimmedFamily = familyName.trim();

    if (!trimmedGiven) {
      setError('Given name is required');
      return;
    }

    const displayName = trimmedFamily
      ? `${trimmedGiven} ${trimmedFamily}`
      : trimmedGiven;

    setLoading(true);

    try {
      await authService.updateUserProfile(displayName);
      setSuccess(true);
      // The auth state will update automatically, but we need to force a refresh
      // by reloading the current user
      window.location.reload();
    } catch (err: unknown) {
      console.error('Profile update error:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const providerInfo = user?.providerData[0];
  const authProvider = providerInfo?.providerId === 'password'
    ? 'Email/Password'
    : providerInfo?.providerId === 'google.com'
    ? 'Google'
    : providerInfo?.providerId === 'microsoft.com'
    ? 'Microsoft'
    : providerInfo?.providerId || 'Unknown';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Profile</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Profile updated successfully!
            </Alert>
          )}

          {/* User info section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              src={user?.photoURL || undefined}
              alt={user?.displayName || 'User'}
              sx={{ width: 64, height: 64, mr: 2 }}
            >
              {user?.displayName?.[0] || user?.email?.[0] || 'U'}
            </Avatar>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Signed in with {authProvider}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={2}>
            <TextField
              label="Given Name"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              required
              fullWidth
              disabled={loading || success}
              autoFocus
              placeholder="John"
            />
            <TextField
              label="Family Name"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              fullWidth
              disabled={loading || success}
              placeholder="Doe"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || success || !givenName.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
